import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import { setCurrentUser } from './user.duck';
import { toFavouriteRecord } from '../util/garageFavourites';

/**
 * Saves the customer's ranked favourites to their profile.
 *
 * Follows the trade-in duck: the write goes to `privateData` through the SDK,
 * and the returned user is pushed into the user slice so every reader of
 * `currentUser` sees the new ranking without a refetch. Its own slice keeps the
 * My Garage page's save state away from the profile page's.
 */

export const saveFavouritesPayloadCreator = async (ids, thunkAPI) => {
  const { extra: sdk, dispatch, rejectWithValue } = thunkAPI;

  try {
    const response = await sdk.currentUser.updateProfile(
      { privateData: { garageFavourites: toFavouriteRecord(ids) } },
      { expand: true }
    );
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }
    dispatch(setCurrentUser(entities[0]));
    return true;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const saveFavouritesThunk = createAsyncThunk(
  'app/garageFavourites/save',
  saveFavouritesPayloadCreator
);

export const saveFavourites = ids => dispatch => dispatch(saveFavouritesThunk(ids));

const initialState = {
  saveInProgress: false,
  saveError: null,
  saved: false,
};

const garageFavouritesSlice = createSlice({
  name: 'app/garageFavourites',
  initialState,
  reducers: {
    resetFavouritesSaveState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(saveFavouritesThunk.pending, state => {
        state.saveInProgress = true;
        state.saveError = null;
        state.saved = false;
      })
      .addCase(saveFavouritesThunk.fulfilled, state => {
        state.saveInProgress = false;
        state.saved = true;
      })
      .addCase(saveFavouritesThunk.rejected, (state, action) => {
        state.saveInProgress = false;
        state.saveError = action.payload;
        state.saved = false;
      });
  },
});

export const { resetFavouritesSaveState } = garageFavouritesSlice.actions;
export default garageFavouritesSlice.reducer;
